import React, { memo } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from '@mui/material'

export type TopFilterConfig = {
  id: string
  label: string
  options: string[]
  value: string[]
  onChange: (value: string[]) => void
  renderOption?: (value: string) => string
  minWidth?: number
  maxWidth?: number
}

type TopFiltersProps = {
  filters: TopFilterConfig[]
}

const DEFAULT_MIN_WIDTH = 100
const DEFAULT_MAX_WIDTH = 130

/**
 * Reusable row of top-level select filters used above Material React Table.
 * Keeps width heuristics and renderValue logic consistent across pages.
 */
export const TopFilters = memo<TopFiltersProps>(({ filters }) => {
  if (!filters.length) return null

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {filters.map((filter) => {
        const minWidth = filter.minWidth ?? DEFAULT_MIN_WIDTH
        const maxWidth = filter.maxWidth ?? DEFAULT_MAX_WIDTH

        return (
          <FormControl key={filter.id} size="small" style={{ minWidth, maxWidth }}>
            <InputLabel id={`${filter.id}-label`}>{filter.label}</InputLabel>
            <Select
              labelId={`${filter.id}-label`}
              label={filter.label}
              multiple
              value={filter.value}
              onChange={(e) => {
                const incoming = e.target.value
                const next = typeof incoming === 'string'
                  ? (incoming as string).split(',')
                  : (incoming as string[])
                filter.onChange(next)
              }}
              renderValue={(selected) => {
                const values = selected as string[]
                const render = filter.renderOption
                if (render) {
                  return values.length > 2
                    ? `${values.length} selected`
                    : values.map((v) => render(v)).join(', ')
                }
                return values.length > 2
                  ? `${values.length} selected`
                  : values.map((v) => v || '(blank)').join(', ')
              }}
              sx={{ height: 36, '& .MuiSelect-select': { paddingTop: '8px', paddingBottom: '8px' } }}
            >
              {filter.options.map((val, index) => (
                <MenuItem key={`${filter.id}-${index}`} value={val}>
                  <Checkbox checked={filter.value.indexOf(val) > -1} />
                  <ListItemText primary={filter.renderOption ? filter.renderOption(val) : (val || '(blank)')} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      })}
    </div>
  )
})

TopFilters.displayName = 'TopFilters'

